/**
 * Exemplar Library
 * Paginated list of exemplar cards with status badges, filters, actions, and test retrieval.
 */

import { useState, useRef, useCallback } from 'react'
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Zap,
  ChevronLeft,
  ChevronRight,
  Upload,
  FileText,
  X,
  Undo2,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  useExemplarsWithPolling,
  useDeleteExemplar,
  useToggleExemplar,
  useTestRetrieval,
  useBulkImportExemplars,
  useBulkImportFromPdf,
  useDeleteBatch,
} from '@/hooks'
import { ExemplarEditor } from './ExemplarEditor'
import type { Exemplar, ExemplarCategory } from '@/lib/repositories'

const PAGE_SIZE = 20

const CATEGORY_LABELS: Record<ExemplarCategory, string> = {
  general: 'General',
  signal_explanation: 'Signal',
  trading_philosophy: 'Philosophy',
  regime_transition: 'Regime',
  confirmation_logic: 'Confirmation',
  ticker_specific: 'Ticker',
  discipline: 'Discipline',
  entries_exits: 'Entries & Exits',
  psychology: 'Psychology',
  risk_management: 'Risk',
}

const CATEGORY_COLORS: Record<ExemplarCategory, string> = {
  general: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20',
  signal_explanation: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  trading_philosophy: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  regime_transition: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  confirmation_logic: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  ticker_specific: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
  discipline: 'bg-red-500/10 text-red-400 border-red-500/20',
  entries_exits: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  psychology: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  risk_management: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
}

interface ParsedExemplarRow {
  question: string
  answer: string
  category: string
  tags: string[]
  error?: string
}

function parseCSVContent(text: string): ParsedExemplarRow[] {
  const lines = text.split('\n')
  if (lines.length < 2) return []

  // Parse header
  const header = (lines[0] ?? '').toLowerCase().trim()
  const headerCols = header.split(',').map((h) => h.trim().replace(/^"|"$/g, ''))

  const qIdx = headerCols.indexOf('question')
  const aIdx = headerCols.indexOf('answer')
  const cIdx = headerCols.indexOf('category')
  const tIdx = headerCols.indexOf('tags')

  if (qIdx === -1 || aIdx === -1) {
    return [{ question: '', answer: '', category: '', tags: [], error: 'CSV must have "question" and "answer" columns' }]
  }

  const rows: ParsedExemplarRow[] = []

  for (let i = 1; i < lines.length; i++) {
    const line = (lines[i] ?? '').trim()
    if (!line) continue

    // Parse CSV line handling quoted fields
    const fields: string[] = []
    let current = ''
    let inQuotes = false

    for (let j = 0; j < line.length; j++) {
      const char = line[j]
      if (char === '"') {
        if (inQuotes && line[j + 1] === '"') {
          current += '"'
          j++
        } else {
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        fields.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    fields.push(current.trim())

    const question = fields[qIdx]?.replace(/^"|"$/g, '') || ''
    const answer = fields[aIdx]?.replace(/^"|"$/g, '') || ''
    const category = cIdx !== -1 ? (fields[cIdx]?.replace(/^"|"$/g, '') || 'general') : 'general'
    const tagsRaw = tIdx !== -1 ? (fields[tIdx]?.replace(/^"|"$/g, '') || '') : ''
    const tags = tagsRaw ? tagsRaw.split('|').map((t) => t.trim()).filter(Boolean) : []

    const errors: string[] = []
    if (!question) errors.push('missing question')
    if (!answer) errors.push('missing answer')
    if (!VALID_CATEGORIES_SET.has(category)) errors.push(`invalid category "${category}"`)

    rows.push({
      question,
      answer,
      category,
      tags,
      error: errors.length > 0 ? errors.join(', ') : undefined,
    })
  }

  return rows
}

const VALID_CATEGORIES_SET = new Set(Object.keys(CATEGORY_LABELS))

export function ExemplarLibrary() {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useExemplarsWithPolling(page, PAGE_SIZE)

  const deleteMutation = useDeleteExemplar()
  const toggleMutation = useToggleExemplar()
  const testRetrievalMutation = useTestRetrieval()
  const bulkImportMutation = useBulkImportExemplars()
  const bulkImportPdfMutation = useBulkImportFromPdf()
  const deleteBatchMutation = useDeleteBatch()

  // Bulk import state
  const [bulkImportOpen, setBulkImportOpen] = useState(false)
  const [parsedRows, setParsedRows] = useState<ParsedExemplarRow[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [lastBatchId, setLastBatchId] = useState<string | null>(null)
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editingExemplar, setEditingExemplar] = useState<Exemplar | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [testQuery, setTestQuery] = useState('')
  const [testResults, setTestResults] = useState<
    { id: string; question: string; similarity: number }[] | null
  >(null)
  const [filterCategory, setFilterCategory] = useState<
    ExemplarCategory | 'all'
  >('all')
  const [filterActive, setFilterActive] = useState<
    'all' | 'active' | 'inactive'
  >('all')

  const exemplars = data?.data || []
  const total = data?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // Client-side filtering for search/category/active
  const filtered = exemplars.filter((e) => {
    if (
      searchQuery &&
      !e.question.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false
    if (filterCategory !== 'all' && e.category !== filterCategory) return false
    if (filterActive === 'active' && !e.isActive) return false
    if (filterActive === 'inactive' && e.isActive) return false
    return true
  })

  const handleDelete = async (exemplar: Exemplar) => {
    if (!confirm(`Delete exemplar "${exemplar.question.slice(0, 60)}..."?`))
      return
    try {
      await deleteMutation.mutateAsync(exemplar.id)
      toast.success('Exemplar deleted')
    } catch (error) {
      toast.error(
        `Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  const handleToggle = async (exemplar: Exemplar) => {
    try {
      await toggleMutation.mutateAsync({
        id: exemplar.id,
        isActive: !exemplar.isActive,
      })
      toast.success(
        exemplar.isActive ? 'Exemplar deactivated' : 'Exemplar activated'
      )
    } catch (error) {
      toast.error(
        `Toggle failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  const handleTestRetrieval = async () => {
    if (!testQuery.trim()) return
    try {
      const results = await testRetrievalMutation.mutateAsync(testQuery.trim())
      setTestResults(
        results.map((r) => ({
          id: r.id,
          question: r.question,
          similarity: r.similarity,
        }))
      )
    } catch (error) {
      toast.error(
        `Test retrieval failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  const handleEdit = (exemplar: Exemplar) => {
    setEditingExemplar(exemplar)
    setEditorOpen(true)
  }

  const handleNewExemplar = () => {
    setEditingExemplar(null)
    setEditorOpen(true)
  }

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const ext = file.name.split('.').pop()?.toLowerCase()

    if (ext === 'csv') {
      try {
        const text = await file.text()
        const rows = parseCSVContent(text)
        setSelectedFile(file)
        setParsedRows(rows)
      } catch (err) {
        toast.error(`Failed to read CSV: ${err instanceof Error ? err.message : 'Unknown error'}`)
        return
      }
    } else if (ext === 'pdf') {
      // PDF is handled server-side, show file info only
      setSelectedFile(file)
      setParsedRows([])
    } else {
      toast.error('Unsupported file type. Use CSV or PDF.')
      return
    }

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const handleBulkImport = async () => {
    if (!selectedFile) return

    const ext = selectedFile.name.split('.').pop()?.toLowerCase()

    try {
      if (ext === 'csv') {
        const validRows = parsedRows.filter((r) => !r.error)
        if (validRows.length === 0) {
          toast.error('No valid rows to import')
          return
        }

        const result = await bulkImportMutation.mutateAsync(
          validRows.map((r) => ({
            question: r.question,
            answer: r.answer,
            category: r.category as ExemplarCategory,
            tags: r.tags,
          }))
        )

        setLastBatchId(result.batchId)
        toast.success(
          `Imported ${result.imported} exemplar${result.imported !== 1 ? 's' : ''}` +
          (result.skippedDuplicates > 0 ? `, ${result.skippedDuplicates} duplicates skipped` : '') +
          (result.failed > 0 ? `, ${result.failed} failed` : '')
        )
      } else if (ext === 'pdf') {
        const result = await bulkImportPdfMutation.mutateAsync({
          file: selectedFile,
          defaultCategory: 'general',
        })

        setLastBatchId(result.batchId)
        toast.success(
          `Parsed ${result.parsedCount ?? 0} Q&A pairs, imported ${result.imported}` +
          (result.skippedDuplicates > 0 ? `, ${result.skippedDuplicates} duplicates skipped` : '') +
          (result.failed > 0 ? `, ${result.failed} failed` : '')
        )
      }

      // Reset state
      setBulkImportOpen(false)
      setSelectedFile(null)
      setParsedRows([])
    } catch (error) {
      toast.error(
        `Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  const handleUndoImport = async () => {
    if (!lastBatchId) return
    try {
      const count = await deleteBatchMutation.mutateAsync(lastBatchId)
      toast.success(`Deleted ${count} exemplar${count !== 1 ? 's' : ''}`)
      setLastBatchId(null)
    } catch (error) {
      toast.error(
        `Undo failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setUndoConfirmOpen(false)
    }
  }

  const handleCloseBulkImport = () => {
    setBulkImportOpen(false)
    setSelectedFile(null)
    setParsedRows([])
  }

  const getStatusBadge = (exemplar: Exemplar) => {
    switch (exemplar.status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-500/10 text-neutral-400 border border-neutral-500/20">
            <Clock className="w-2.5 h-2.5" />
            Pending
          </span>
        )
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <Loader2 className="w-2.5 h-2.5 animate-spin" />
            Processing
          </span>
        )
      case 'ready':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <CheckCircle2 className="w-2.5 h-2.5" />
            Ready
          </span>
        )
      case 'failed':
        return (
          <span
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20"
            title={exemplar.errorMessage || 'Embedding failed'}
          >
            <AlertCircle className="w-2.5 h-2.5" />
            Failed
          </span>
        )
    }
  }

  return (
    <div className="space-y-4">
      {/* Header + Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-neutral-400">
          {total} exemplar{total !== 1 ? 's' : ''}
        </div>
        <div className="flex items-center gap-2">
          {lastBatchId && (
            <Button
              onClick={() => setUndoConfirmOpen(true)}
              disabled={deleteBatchMutation.isPending}
              variant="ghost"
              className="h-8 px-3 text-xs text-neutral-400 hover:text-red-400"
            >
              {deleteBatchMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Undo2 className="w-3.5 h-3.5 mr-1.5" />
              )}
              Undo Import
            </Button>
          )}
          <Button
            onClick={() => setBulkImportOpen(true)}
            variant="ghost"
            className="h-8 px-3 text-xs text-neutral-400 hover:text-cyan-400 border border-white/[0.06]"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Bulk Import
          </Button>
          <Button
            onClick={handleNewExemplar}
            className="bg-gradient-to-br from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-semibold h-8 px-3 text-xs shadow-lg shadow-emerald-500/25"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add New
          </Button>
        </div>
      </div>

      {/* Bulk Import Panel */}
      {bulkImportOpen && (
        <div className="p-4 rounded-lg bg-white/[0.02] border border-cyan-500/20 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-white flex items-center gap-2">
              <Upload className="w-4 h-4 text-cyan-400" />
              Bulk Import Exemplars
            </h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseBulkImport}
              className="h-6 w-6 text-neutral-500 hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>

          <p className="text-xs text-neutral-500">
            Upload a CSV (columns: question, answer, category, tags) or a PDF with Q&A pairs.
            Tags should be pipe-separated (e.g., risk|volatility|macro).
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.pdf"
            onChange={handleFileSelect}
            className="hidden"
          />

          {!selectedFile ? (
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="ghost"
              className="w-full h-20 border-2 border-dashed border-white/[0.08] hover:border-cyan-500/30 text-neutral-400 hover:text-cyan-400"
            >
              <div className="flex flex-col items-center gap-1.5">
                <FileText className="w-6 h-6" />
                <span className="text-xs">Choose CSV or PDF file</span>
              </div>
            </Button>
          ) : (
            <div className="space-y-3">
              {/* Selected file info */}
              <div className="flex items-center justify-between px-3 py-2 rounded bg-white/[0.04] border border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs text-neutral-300">{selectedFile.name}</span>
                  <span className="text-[10px] text-neutral-500">
                    ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setSelectedFile(null); setParsedRows([]) }}
                  className="h-5 w-5 text-neutral-500 hover:text-white"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>

              {/* CSV Preview */}
              {selectedFile.name.endsWith('.csv') && parsedRows.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-neutral-400">
                      {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} parsed
                    </span>
                    <span className="text-emerald-400">
                      {parsedRows.filter((r) => !r.error).length} valid
                    </span>
                    {parsedRows.some((r) => r.error) && (
                      <span className="text-red-400">
                        {parsedRows.filter((r) => r.error).length} with errors
                      </span>
                    )}
                  </div>

                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {parsedRows.slice(0, 20).map((row, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          'px-2.5 py-1.5 rounded text-xs border',
                          row.error
                            ? 'bg-red-500/5 border-red-500/20'
                            : 'bg-white/[0.02] border-white/[0.04]'
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-neutral-300 truncate flex-1">
                            {row.question || '(empty)'}
                          </span>
                          {row.error ? (
                            <span className="text-red-400 text-[10px] flex-shrink-0">
                              {row.error}
                            </span>
                          ) : (
                            <span
                              className={cn(
                                'text-[10px] px-1 py-0.5 rounded border flex-shrink-0',
                                CATEGORY_COLORS[row.category as ExemplarCategory] || 'text-neutral-400'
                              )}
                            >
                              {CATEGORY_LABELS[row.category as ExemplarCategory] || row.category}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {parsedRows.length > 20 && (
                      <p className="text-[10px] text-neutral-500 text-center py-1">
                        ...and {parsedRows.length - 20} more
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* PDF info */}
              {selectedFile.name.endsWith('.pdf') && (
                <p className="text-xs text-neutral-500">
                  PDF will be processed server-side. GPT-4o-mini will extract Q&A pairs and preserve tags automatically.
                </p>
              )}

              {/* Import button */}
              <Button
                onClick={handleBulkImport}
                disabled={
                  bulkImportMutation.isPending ||
                  bulkImportPdfMutation.isPending ||
                  (selectedFile.name.endsWith('.csv') && parsedRows.filter((r) => !r.error).length === 0)
                }
                className="w-full bg-gradient-to-br from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-black font-semibold h-9 text-xs shadow-lg shadow-cyan-500/25"
              >
                {bulkImportMutation.isPending || bulkImportPdfMutation.isPending ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    {selectedFile.name.endsWith('.pdf') ? 'Parsing & Importing...' : 'Importing...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                    {selectedFile.name.endsWith('.csv')
                      ? `Import ${parsedRows.filter((r) => !r.error).length} Exemplars`
                      : 'Parse & Import from PDF'}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="space-y-2">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search questions..."
            className="bg-white/[0.04] border-white/[0.06] text-white placeholder:text-neutral-500 text-sm pl-9 h-9"
          />
        </div>

        {/* Category + Active filters */}
        <div className="flex gap-2">
          <select
            value={filterCategory}
            onChange={(e) =>
              setFilterCategory(e.target.value as ExemplarCategory | 'all')
            }
            className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-2.5 py-1.5 text-xs text-neutral-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 appearance-none cursor-pointer"
          >
            <option value="all" className="bg-neutral-900">
              All Categories
            </option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value} className="bg-neutral-900">
                {label}
              </option>
            ))}
          </select>

          <select
            value={filterActive}
            onChange={(e) =>
              setFilterActive(e.target.value as 'all' | 'active' | 'inactive')
            }
            className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-2.5 py-1.5 text-xs text-neutral-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 appearance-none cursor-pointer"
          >
            <option value="all" className="bg-neutral-900">
              All Status
            </option>
            <option value="active" className="bg-neutral-900">
              Active
            </option>
            <option value="inactive" className="bg-neutral-900">
              Inactive
            </option>
          </select>
        </div>
      </div>

      {/* Test Retrieval */}
      <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] space-y-2">
        <label className="text-xs font-medium text-neutral-400 flex items-center gap-1.5">
          <Zap className="w-3 h-3 text-amber-400" />
          Test Retrieval
        </label>
        <div className="flex gap-2">
          <Input
            value={testQuery}
            onChange={(e) => setTestQuery(e.target.value)}
            placeholder="Enter a question to test matching..."
            className="bg-white/[0.04] border-white/[0.06] text-white placeholder:text-neutral-500 text-sm h-8"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleTestRetrieval()
            }}
          />
          <Button
            onClick={handleTestRetrieval}
            disabled={!testQuery.trim() || testRetrievalMutation.isPending}
            className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/20 h-8 px-3 text-xs"
          >
            {testRetrievalMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              'Test'
            )}
          </Button>
        </div>
        {testResults && (
          <div className="space-y-1.5 mt-2">
            {testResults.length === 0 ? (
              <p className="text-xs text-neutral-500">
                No matches found above threshold
              </p>
            ) : (
              testResults.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between px-2.5 py-1.5 rounded bg-white/[0.02] border border-white/[0.04] text-xs"
                >
                  <span className="text-neutral-300 truncate mr-2">
                    {r.question}
                  </span>
                  <span
                    className={cn(
                      'flex-shrink-0 font-mono',
                      r.similarity >= 0.7
                        ? 'text-emerald-400'
                        : r.similarity >= 0.5
                          ? 'text-amber-400'
                          : 'text-neutral-500'
                    )}
                  >
                    {(r.similarity * 100).toFixed(1)}%
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Exemplar List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-neutral-500 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Zap className="w-12 h-12 text-neutral-700 mx-auto mb-3" />
          <p className="text-neutral-400 text-sm">
            {exemplars.length === 0
              ? 'No exemplars yet'
              : 'No exemplars match your filters'}
          </p>
          <p className="text-neutral-500 text-xs mt-1">
            {exemplars.length === 0
              ? 'Create your first exemplar to start training the AI'
              : 'Try adjusting your search or filters'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((exemplar) => (
            <div
              key={exemplar.id}
              className={cn(
                'p-3 rounded-lg border transition-colors',
                exemplar.isActive
                  ? 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.04]'
                  : 'bg-white/[0.01] border-white/[0.02] opacity-60'
              )}
            >
              {/* Top row: category + status + actions */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded border',
                      CATEGORY_COLORS[exemplar.category]
                    )}
                  >
                    {CATEGORY_LABELS[exemplar.category]}
                  </span>
                  {getStatusBadge(exemplar)}
                  {!exemplar.isActive && (
                    <span className="text-[10px] text-neutral-500">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleToggle(exemplar)}
                    className="h-7 w-7 text-neutral-500 hover:text-white"
                    title={
                      exemplar.isActive
                        ? 'Deactivate'
                        : 'Activate'
                    }
                  >
                    {exemplar.isActive ? (
                      <ToggleRight className="w-3.5 h-3.5 text-emerald-400" />
                    ) : (
                      <ToggleLeft className="w-3.5 h-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(exemplar)}
                    className="h-7 w-7 text-neutral-500 hover:text-cyan-400"
                    title="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(exemplar)}
                    disabled={deleteMutation.isPending}
                    className="h-7 w-7 text-neutral-500 hover:text-red-400 hover:bg-red-500/10"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Question */}
              <p className="text-sm text-white font-medium leading-snug line-clamp-2">
                {exemplar.question}
              </p>

              {/* Answer preview */}
              <p className="text-xs text-neutral-400 mt-1.5 line-clamp-2 leading-relaxed">
                {exemplar.answer}
              </p>

              {/* Tags */}
              {exemplar.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {exemplar.tags.map((tag) => (
                    <span
                      key={tag}
                      className={cn(
                        'text-[9px] px-1.5 py-0.5 rounded-full border',
                        tag.startsWith('batch:')
                          ? 'bg-cyan-500/5 text-cyan-500/60 border-cyan-500/10'
                          : 'bg-white/[0.04] text-neutral-500 border-white/[0.04]'
                      )}
                    >
                      {tag.startsWith('batch:') ? 'bulk import' : tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Error message */}
              {exemplar.status === 'failed' && exemplar.errorMessage && (
                <p
                  className="text-xs text-red-400/80 mt-1.5 truncate"
                  title={exemplar.errorMessage}
                >
                  {exemplar.errorMessage}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="h-8 w-8 text-neutral-400 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-neutral-500 tabular-nums">
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="h-8 w-8 text-neutral-400 hover:text-white"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Exemplar Editor Sheet */}
      <ExemplarEditor
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false)
          setEditingExemplar(null)
        }}
        exemplar={editingExemplar}
      />

      {/* Undo Bulk Import Confirmation */}
      <ConfirmDialog
        open={undoConfirmOpen}
        onOpenChange={setUndoConfirmOpen}
        title="Undo bulk import?"
        description="This will permanently delete all exemplars from the last batch import. This action cannot be undone."
        confirmLabel="Delete Batch"
        variant="danger"
        loading={deleteBatchMutation.isPending}
        onConfirm={handleUndoImport}
      />
    </div>
  )
}
